/* Independent lifecycle: editor re-renders reconnect without duplicate listeners. */
if (!customElements.get('sunco-key-specs-tiles')) {
  customElements.define('sunco-key-specs-tiles', class extends HTMLElement {
    balanceTiles() {
      const container = this.querySelector('.sunco-kst__tiles');
      if (!container) return;
      // Re-measure natural flex layout; never observe this height-changing container.
      container.classList.remove('sunco-kst__tiles--grid');
      container.style.removeProperty('--kst-cols');
      if (!container.clientWidth) return;
      const tiles = [...container.querySelectorAll('.sunco-kst__tile')]
        .filter((tile) => !tile.classList.contains('sunco-kst__tile--full'));
      if (tiles.length < 4) return;
      const firstTop = Math.round(tiles[0].offsetTop);
      if (!tiles.some((tile) => Math.round(tile.offsetTop) !== firstTop)) return;
      let columns = container.clientWidth < 480 ? 2 : Math.ceil(tiles.length / 2);
      for (; columns >= 1; columns--) {
        container.style.setProperty('--kst-cols', columns);
        container.classList.add('sunco-kst__tiles--grid');
        if (container.scrollWidth <= container.clientWidth + 1) break;
      }
    }
    connectedCallback() {
      if (this.controller || this.initFrame) return;
      // A previously registered element can connect before the parser adds its children.
      this.initFrame = requestAnimationFrame(() => {
        this.initFrame = null;
        this.initialize();
      });
    }
    initialize() {
      if (!this.isConnected || this.controller) return;
      const wrapper = this.querySelector('[data-sunco-kst-strip]');
      const strip = this.querySelector('.sunco-kst__strip');
      if (!wrapper || !strip) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const balance = () => this.balanceTiles();
      window.addEventListener('resize', () => {
        clearTimeout(this.balanceTimer);
        this.balanceTimer = setTimeout(balance, 100);
      }, { passive: true, signal });
      window.addEventListener('load', balance, { once: true, signal });
      document.addEventListener('shopify:block:load', balance, { signal });
      document.addEventListener('shopify:section:load', balance, { signal });
      const previous = this.querySelector('[data-sunco-kst-scroll="prev"]');
      const next = this.querySelector('[data-sunco-kst-scroll="next"]');
      const update = () => {
        const scrollable = strip.scrollWidth > strip.clientWidth + 1;
        const atStart = strip.scrollLeft <= 2;
        const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
        const mobile = matchMedia('(max-width: 749px)').matches;
        wrapper.classList.toggle('is-scrollable', scrollable);
        wrapper.classList.toggle('is-at-start', atStart);
        wrapper.classList.toggle('is-at-end', atEnd);
        for (const [button, hide] of [[previous, !mobile || !scrollable || atStart], [next, !mobile || !scrollable || atEnd]]) {
          if (!button) continue;
          if (hide && document.activeElement === button && mobile) strip.focus({ preventScroll: true });
          button.hidden = hide;
        }
      };
      const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
      strip.addEventListener('scroll', update, { passive: true, signal });
      window.addEventListener('resize', update, { passive: true, signal });
      window.addEventListener('load', update, { once: true, signal });
      document.addEventListener('shopify:section:load', update, { signal });
      for (const [button, direction] of [[previous, -1], [next, 1]]) {
        button?.addEventListener('click', () => {
          strip.scrollBy({ left: direction * strip.clientWidth * 0.8, behavior: behavior() });
        }, { signal });
      }
      this.querySelectorAll('[data-sunco-kst-more]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const target = document.getElementById('pdp_sp_main');
          if (!target) return;
          event.preventDefault();
          for (let ancestor = target; ancestor; ancestor = ancestor.parentElement) {
            if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
          }
          target.querySelectorAll('details').forEach((details) => { details.open = true; });
          const toggle = target.querySelector('[data-sunco-spec-toggle]');
          if (toggle && !toggle.hidden && toggle.getAttribute('aria-expanded') === 'false') toggle.click();
          target.style.scrollMarginTop = 'calc(var(--header-height, 72px) + 12px)';
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: behavior(), block: 'start' });
          });
        }, { signal });
      });
      this.observer = new ResizeObserver(update);
      this.observer.observe(strip);
      update();
      balance();
      document.fonts?.ready.then(() => { if (!signal.aborted && this.isConnected) { update(); balance(); } });
    }
    disconnectedCallback() {
      clearTimeout(this.balanceTimer);
      cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  });
}
